import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";

import {
  getStationSubscribers,
  messageForError,
  stateForError,
  StationSubscriber,
} from "../../services/notificationSettings";
import { getCochargeUserByEmaid, getStationOwners } from "../../services/owners";
import { PanelState } from "../../types/notificationSettings";

interface Row extends StationSubscriber {
  email: string | null;
  isOwner: boolean;
}

/** Who is watching one station, and which of them own it. */
const StationSubscriberSearch = () => {
  const navigate = useNavigate();
  const [draft, setDraft] = useState("");
  const [station, setStation] = useState<string | null>(null);
  const [state, setState] = useState<PanelState>("empty");
  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [ownersUnavailable, setOwnersUnavailable] = useState(false);

  const search = async (stationId: string) => {
    setStation(stationId);
    setState("loading");
    setError(null);
    setOwnersUnavailable(false);

    try {
      const subscribers = await getStationSubscribers(stationId);

      const owners = await getStationOwners(stationId).catch(() => {
        setOwnersUnavailable(true);
        return [] as any[];
      });
      const ownerEmails = new Set(
        (owners ?? []).map((o: any) => String(o?.email ?? "").toLowerCase()).filter(Boolean),
      );
      const ownerIds = new Set(
        (owners ?? []).map((o: any) => o?.emaid ?? o?.user_id).filter(Boolean),
      );

      const enriched = await Promise.all(
        subscribers.map(async (s) => {
          const user = await getCochargeUserByEmaid(s.userId).catch(() => null);
          const email = user?.email ?? null;
          return {
            ...s,
            email,
            isOwner:
              ownerIds.has(s.userId) ||
              (email ? ownerEmails.has(email.toLowerCase()) : false),
          };
        }),
      );

      setRows(enriched);
      setState(enriched.length ? "ready" : "empty");
    } catch (e) {
      setError(messageForError(e));
      setState(stateForError(e));
    }
  };

  return (
    <div className="flex flex-column gap-3">
      <form
        className="flex align-items-end gap-2 flex-wrap"
        onSubmit={(e) => {
          e.preventDefault();
          const trimmed = draft.trim();
          if (trimmed) void search(trimmed);
        }}
      >
        <div className="flex flex-column gap-2">
          <label htmlFor="lookup-station" className="text-sm">Station</label>
          <InputText
            id="lookup-station"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="DECOCMALAGA"
            className="w-24rem"
          />
        </div>
        <Button type="submit" label="Who is subscribed" icon="pi pi-search" disabled={!draft.trim()} />
      </form>

      {state === "loading" && (
        <div className="flex align-items-center gap-3 py-3" data-testid="subscribers-loading">
          <ProgressSpinner style={{ width: "2rem", height: "2rem" }} strokeWidth="4" />
          <span className="text-color-secondary">Looking up subscribers…</span>
        </div>
      )}

      {state === "refused" && (
        <div data-testid="subscribers-refused">
          <Message severity="warn" text="Seeing who is subscribed requires an administrator entitlement." />
        </div>
      )}

      {state === "failed" && (
        <div data-testid="subscribers-failed">
          <Message severity="error" text={`Could not load subscribers${error ? `: ${error}` : ""}`} />
        </div>
      )}

      {state === "empty" && station && (
        <div data-testid="subscribers-empty">
          <Message severity="info" text={`Nobody is subscribed to ${station}.`} />
        </div>
      )}

      {state === "ready" && (
        <div data-testid="subscribers-ready">
          {ownersUnavailable && (
            <div className="mb-2" data-testid="owners-unavailable">
              <Message
                severity="warn"
                text="Could not load this station's owners, so nobody is marked as one. The subscriber list itself is complete."
              />
            </div>
          )}
          <DataTable value={rows} dataKey="userId" paginator={false} className="p-datatable-sm">
            <Column
              header="Subscriber"
              body={(r: Row) => (
                <div className="flex flex-column">
                  <span>{r.userId}</span>
                  {r.email && <span className="text-xs text-color-secondary">{r.email}</span>}
                </div>
              )}
            />
            <Column
              header="Owns this station"
              body={(r: Row) =>
                r.isOwner ? (
                  <Tag severity="success" value="owner" data-testid={`owner-${r.userId}`} />
                ) : (
                  <span className="text-color-secondary text-sm">driver</span>
                )
              }
            />
            <Column
              header="Chargers"
              body={(r: Row) =>
                r.evses.length ? r.evses.join(", ") : <span className="text-color-secondary">whole station</span>
              }
            />
            <Column header="Statuses" body={(r: Row) => r.statuses.join(", ")} />
            <Column
              header=""
              body={(r: Row) => (
                <Button
                  icon="pi pi-arrow-right"
                  text
                  aria-label={`Open ${r.userId}`}
                  onClick={() => navigate(`/notifications/subscriptions/${encodeURIComponent(r.userId)}`)}
                />
              )}
            />
          </DataTable>
        </div>
      )}
    </div>
  );
};

export default StationSubscriberSearch;
